import { Info, Lock, Plus, Save, Star, Trash2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { MAX_ENTRY_SECONDS, formatDuration, normalizeDuration, parseDuration, periodMonthName } from '@cfl/shared';

import {
  Alert, AlertDescription, Badge, Button, Combobox, Input,
  Tooltip, TooltipContent, TooltipTrigger, toast,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { ClockArt } from '@/components/illustrations';
import { ApiError } from '@/lib/api';
import { fitsProject, useActivities, useProjects } from '@/lib/catalog-api';
import { useSaveGrid, useSetFavorite, type GridSuggestion, type WeekGrid } from '@/lib/grid-api';
import { setUnsavedWork } from '@/lib/reload-on-deploy';
import { DURATION_LIST_ID, DurationPresets } from './duration-presets';

/** One row being edited. Durations are held as TEXT, not seconds - see below. */
interface DraftRow {
  key: string;
  projectId: string;
  activityId: string;
  description: string | null;
  /** Pinned: the server puts this row on every week. Not part of what is saved. */
  favorite: boolean;
  /** Date -> what the person typed. */
  cells: Record<string, string>;
}

/**
 * The week as a grid: a row per project/activity pairing, a column per day.
 *
 * The point of this layout is that the pairing is chosen once and then seven
 * numbers are typed. A dialog per entry turns recording a normal week into
 * thirty-five interactions.
 *
 * Cells hold TEXT while being edited rather than parsed seconds. Somebody typing
 * "7:3" is mid-way through "7:30", and a grid that reformats or rejects on every
 * keystroke is unusable - so the text is kept as typed, shown as invalid if it
 * cannot be read, and only converted on save.
 */
/**
 * What a draft would leave on the server, to tell unsaved typing from saved.
 *
 * Compared as seconds, not as the text typed, and without rows that carry no
 * hours: "8" over a saved 8:00, or an added row left empty, saves nothing, so the
 * server's copy comes back identical, the grid never reloads, and a text
 * comparison would keep Submit disabled after Save forever. It did.
 */
const savedShape = (rows: DraftRow[]) =>
  JSON.stringify(
    rows
      .map((r) => {
        const cells = Object.entries(r.cells)
          .map(([date, text]) => [date, text.trim() === '' ? 0 : parseDuration(text.trim())] as const)
          .filter(([, v]) => v !== 0)
          .sort(([a], [b]) => a.localeCompare(b));
        return [r.projectId, r.activityId, r.description ?? '', cells] as const;
      })
      .filter(([projectId, activityId, , cells]) => projectId && activityId && cells.length > 0)
      .sort((a, b) => `${a[0]}|${a[1]}`.localeCompare(`${b[0]}|${b[1]}`)),
  );

export function WeekGrid({
  grid,
  onSaved,
  onDirtyChange,
}: {
  grid: WeekGrid;
  onSaved?: () => void;
  /** Typed but not saved. The submit button reads this - see the effect below. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { data: projects } = useProjects();
  const { data: activities } = useActivities();
  const save = useSaveGrid();
  const setFavorite = useSetFavorite();

  const [rows, setRows] = React.useState<DraftRow[]>([]);
  // What the server's copy looks like as drafts, to tell typing from saved.
  const [baseline, setBaseline] = React.useState('[]');

  // Read-only counts as closed. Every editing control below is already gated on
  // this one flag, so somebody else's week needs no second rule - and a second rule
  // is exactly what would drift out of step with this one.
  //
  // A week split at a month end is only closed once EVERY part is: with September
  // submitted and October open, the pickers and the trash stay live, and the
  // submitted days are shut cell by cell from `day.locked` instead.
  const isClosed = (p: { status: string }) => p.status === 'SUBMITTED' || p.status === 'APPROVED';
  const weekClosed = grid.readOnly || grid.periods.every(isClosed);

  // Two parts means the week crosses a month end. The month bands and the divider
  // show where it splits, so it is plain which cells a part's submit covers.
  const split = grid.periods.length > 1;
  const monthStart = split ? grid.periods[1]!.start : null;
  const divider = (date: string) => (date === monthStart ? 'border-l-2 border-l-foreground/25' : '');
  const closedParts = grid.periods.filter(isClosed);

  /** An empty row, ready to have a pairing chosen on it. */
  const blankRow = React.useCallback(
    (): DraftRow => ({
      // A time-based key, so a brand-new row is distinct even before a pairing is
      // chosen and two empty rows do not collide.
      key: `new-${Date.now()}`,
      projectId: '',
      activityId: '',
      description: null,
      favorite: false,
      cells: Object.fromEntries(grid.days.map((d) => [d.date, ''])),
    }),
    [grid.days],
  );

  // Reloaded whenever the server's copy changes - after a save, or when the week
  // changes. Keyed on the week so switching weeks does not carry edits across.
  React.useEffect(() => {
    const loaded = grid.rows.map((row) => ({
      key: `${row.projectId}|${row.activityId}`,
      projectId: row.projectId,
      activityId: row.activityId,
      description: row.description,
      favorite: row.favorite,
      cells: Object.fromEntries(
        grid.days.map((d) => [d.date, row.cells[d.date] ? formatDuration(row.cells[d.date]!) : '']),
      ),
    }));
    // A week you can type in always opens with a row to type in. It used to open
    // on "Nothing recorded this week. Add a row to start." - an empty table, an
    // instruction, and a button to hunt for before the screen does the one thing
    // it is for.
    const initial = loaded.length === 0 && !weekClosed ? [blankRow()] : loaded;
    setRows(initial);
    setBaseline(savedShape(initial));
  }, [grid, weekClosed, blankRow]);

  /*
   * Unsaved typing has to stop the submit and survive a reload attempt.
   *
   * Submit sends what the SERVER holds. On 2026-10-01 people filled a reopened
   * week, pressed Submit without Save, and submitted only what was already
   * stored; the submit then closed the week, the grid reloaded from the server,
   * and every typed cell went back to blank with Save disabled. One of them
   * reloaded the page 34 times trying to get the hours back.
   */
  const dirty = !weekClosed && savedShape(rows) !== baseline;
  React.useEffect(() => {
    onDirtyChange?.(dirty);
    setUnsavedWork(dirty);
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, onDirtyChange]);
  React.useEffect(() => () => {
    onDirtyChange?.(false);
    setUnsavedWork(false);
  }, [onDirtyChange]);

  /*
   * Cells holding detail this grid cannot draw, keyed pairing -> dates.
   *
   * Read from the server's rows rather than the drafts, because it describes what
   * was SAVED. A cell being retyped has not lost its tags; the marker should not
   * flicker while somebody edits the number beside it.
   */
  const detail = React.useMemo(() => {
    const byRow = new Map<string, Set<string>>();
    for (const row of grid.rows) {
      if (row.detailDays.length > 0) {
        byRow.set(`${row.projectId}|${row.activityId}`, new Set(row.detailDays));
      }
    }
    return byRow;
  }, [grid.rows]);

  /** Parsed live, so the totals and the invalid marks are honest as you type. */
  const parsed = React.useMemo(
    () =>
      rows.map((row) =>
        Object.fromEntries(
          grid.days.map((d) => {
            const text = (row.cells[d.date] ?? '').trim();
            return [d.date, text === '' ? 0 : parseDuration(text)];
          }),
        ),
      ),
    [rows, grid.days],
  );

  /**
   * Unreadable, or longer than a day. Both are marked the same way and both stop the
   * save, because the server refuses the long one too - and it refuses the whole
   * cell with a row-level fault, so catching "30" here saves a round trip to be
   * told that thirty hours is not half an hour.
   */
  const cellInvalid = (v: number | null | undefined) => v === null || (v ?? 0) > MAX_ENTRY_SECONDS;

  const anyInvalid = parsed.some((cells) => Object.values(cells).some(cellInvalid));

  /** Hours typed on a row with no project or activity: those hours have nowhere to go. */
  const needsPairing = (i: number) =>
    Object.values(parsed[i] ?? {}).some((v) => (v ?? 0) > 0) && (!rows[i]?.projectId || !rows[i]?.activityId);

  const rowTotal = (i: number): number =>
    Object.values(parsed[i] ?? {}).reduce<number>((sum, v) => sum + (v ?? 0), 0);
  const dayTotal = (date: string): number =>
    parsed.reduce<number>((sum, cells) => sum + (cells[date] ?? 0), 0);
  const weekTotal = grid.days.reduce((sum, d) => sum + dayTotal(d.date), 0);

  const setCell = (rowIndex: number, date: string, value: string) =>
    setRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, cells: { ...row.cells, [date]: value } } : row)),
    );

  /** Add a row already filled in, from a pairing this person books often. */
  const addSuggested = (s: GridSuggestion) =>
    setRows((prev) => [
      ...prev,
      {
        key: `new-${s.projectId}-${s.activityId}-${Date.now()}`,
        projectId: s.projectId,
        activityId: s.activityId,
        description: null,
        favorite: false,
        cells: Object.fromEntries(grid.days.map((d) => [d.date, ''])),
      },
    ]);

  /** Nothing is offered twice: the server already excludes this week's rows, and
      this drops anything added since the page loaded. */
  const available = grid.suggestions.filter(
    (s) => !rows.some((r) => r.projectId === s.projectId && r.activityId === s.activityId),
  );

  const addRow = () => setRows((prev) => [...prev, blankRow()]);

  const submit = async () => {

    if (anyInvalid) {
      toast.error('Some cells could not be read, or are longer than a day. Use 8:00, 7.5 or 90m.');
      return;
    }
    const incomplete = rows.flatMap((row, i) =>
      needsPairing(i) ? [`row ${i + 1} (${!row.projectId ? 'no project' : 'no activity'})`] : [],
    );
    if (incomplete.length > 0) {
      toast.error(
        `Hours were typed on ${incomplete.join(', ')}. Choose a project and an activity for ` +
          `${incomplete.length === 1 ? 'that row' : 'those rows'}, or clear ${incomplete.length === 1 ? 'its' : 'their'} hours, then save again.`,
      );
      return;
    }

    try {
      const result = await save.mutateAsync({
        date: grid.weekStart,
        rows: rows
          // The original index is carried through the filter, so a row's cells always
          // come from the row they belong to - looking the index up again afterwards
          // is the kind of thing that silently pairs the wrong hours with a project.
          .map((row, index) => ({ row, index }))
          // A row with no pairing and no hours is an empty line somebody added and
          // did not use; it carries nothing to save.
          .filter(({ row }) => row.projectId && row.activityId)
          .map(({ row, index }) => ({
            projectId: row.projectId,
            activityId: row.activityId,
            description: row.description,
            cells: Object.fromEntries(
              grid.days.map((d) => [d.date, parsed[index]?.[d.date] ?? 0]),
            ) as Record<string, number>,
          })),
      });
      const parts = [
        result.created ? `${result.created} added` : '',
        result.updated ? `${result.updated} changed` : '',
        result.deleted ? `${result.deleted} removed` : '',
      ].filter(Boolean);
      toast.success(
        `Saved${parts.length ? `: ${parts.join(', ')}` : ' — nothing had changed'}.` +
          (result.skippedLocked
            ? split && closedParts.length > 0
              ? ` Changes on ${days(result.skippedLocked)} of submitted ${closedParts.map(periodMonthName).join(' and ')} were skipped — those hours stay as submitted.`
              : ` Changes on ${days(result.skippedLocked)} that are closed were skipped.`
            : ''),
      );
      // The server now holds this. If nothing changed there the grid does not
      // reload, so the baseline has to move here or Submit stays disabled.
      setBaseline(savedShape(rows));
      onSaved?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save the week.');
    }
  };

  const projectOptions = React.useMemo(
    () =>
      (projects ?? []).map((p) => ({
        value: p.id,
        label: p.name,
        description: p.customer.name,
      })),
    [projects],
  );

  return (
    <div className="space-y-3">
      <DurationPresets />

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          {/* Sticky, because a long list of pairings scrolls past the day names
              and then nobody knows which column they are typing into. */}
          <thead className="sticky top-0 z-10 bg-muted">
            {split && (
              <tr>
                <th colSpan={2} />
                {grid.periods.map((period) => {
                  const days = grid.days.filter((d) => d.date >= period.start && d.date <= period.end);
                  return (
                    <th
                      key={period.start}
                      colSpan={days.length}
                      className={`px-1 pt-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${divider(period.start)}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {isClosed(period) && <Lock className="size-3" aria-label="submitted" />}
                        {periodMonthName(period)}
                      </span>
                    </th>
                  );
                })}
                <th colSpan={2} />
              </tr>
            )}
            <tr>
              <th className="w-[210px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Project
              </th>
              <th className="w-[190px] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Activity
              </th>
              {grid.days.map((day) => (
                <th
                  key={day.date}
                  className={`w-[74px] px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wider ${divider(day.date)} ${
                    day.isToday
                      ? 'text-primary'
                      : day.isWeekend
                        ? 'text-muted-foreground/60'
                        : 'text-muted-foreground'
                  }`}
                >
                  <div>{weekday(day.date)}</div>
                  <div className="ts-num font-normal normal-case">{Number(day.date.slice(8, 10))}</div>
                </th>
              ))}
              <th className="w-[86px] px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Total
              </th>
              <th className="w-[80px]" />
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => (
              <tr key={row.key} className="border-t">
                <td className="px-2 py-1.5">
                  <Combobox
                    value={row.projectId || null}
                    onChange={(v) =>
                      setRows((prev) =>
                        prev.map((r, j) => {
                          if (j !== i) return r;
                          // A project-specific activity may not survive the change.
                          const stillValid = (activities ?? []).some(
                            (a) => a.id === r.activityId && (fitsProject(a, v, projects)),
                          );
                          return { ...r, projectId: v, activityId: stillValid ? r.activityId : '' };
                        }),
                      )
                    }
                    options={projectOptions}
                    placeholder="Project"
                    searchPlaceholder="Search projects"
                    className={needsPairing(i) && !row.projectId ? 'border-destructive' : undefined}
                    disabled={weekClosed}
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Combobox
                    value={row.activityId || null}
                    onChange={(v) =>
                      setRows((prev) => prev.map((r, j) => (j === i ? { ...r, activityId: v } : r)))
                    }
                    options={(activities ?? [])
                      .filter((a) => fitsProject(a, row.projectId, projects))
                      .map((a) => ({
                        value: a.id,
                        label: a.name,
                        hint: a.project ? undefined : 'any project',
                      }))}
                    placeholder={row.projectId ? 'Activity' : 'Project first'}
                    searchPlaceholder="Search activities"
                    className={needsPairing(i) && row.projectId && !row.activityId ? 'border-destructive' : undefined}
                    disabled={weekClosed || !row.projectId}
                  />
                </td>

                {grid.days.map((day) => {
                  const text = row.cells[day.date] ?? '';
                  const invalid = cellInvalid(parsed[i]?.[day.date]);
                  const hasDetail = detail.get(row.key)?.has(day.date) ?? false;
                  return (
                    <td
                      key={day.date}
                      className={`px-1 py-1.5 ${divider(day.date)} ${day.isWeekend ? 'bg-muted/40' : ''} ${
                        day.isToday ? 'bg-primary/5' : ''
                      }`}
                    >
                      <div className="relative">
                        <Input
                          aria-label={`Row ${i + 1}, ${day.date}`}
                          list={DURATION_LIST_ID}
                          value={text}
                          disabled={day.locked || weekClosed}
                          onChange={(e) => setCell(i, day.date, e.target.value)}
                          // On the way out, show what was understood: "7.5" becomes
                          // "7:30" while the cell can still be corrected.
                          onBlur={(e) => setCell(i, day.date, normalizeDuration(e.target.value))}
                          placeholder="—"
                          className={`ts-num h-9 px-1 text-center ${invalid ? 'border-destructive' : ''}`}
                        />
                        {hasDetail && <DetailMark date={day.date} />}
                      </div>
                    </td>
                  );
                })}

                <td className="ts-num px-3 py-1.5 text-right font-medium">
                  {formatDuration(rowTotal(i))}
                </td>
                <td className="whitespace-nowrap px-1 py-1.5">
                  {/* Pinning needs a pairing to pin. An unfinished row has nothing
                      to remember, so the star is absent rather than disabled. */}
                  {!weekClosed && row.projectId && row.activityId && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={row.favorite ? 'Unpin this row' : 'Pin this row to every week'}
                      title={
                        row.favorite
                          ? 'Pinned. It appears on every week - click to unpin.'
                          : 'Pin: keep this row on every week, booked to or not.'
                      }
                      disabled={setFavorite.isPending}
                      onClick={async () => {
                        try {
                          await setFavorite.mutateAsync({
                            projectId: row.projectId,
                            activityId: row.activityId,
                            favorite: !row.favorite,
                          });
                        } catch (err) {
                          toast.error(
                            err instanceof ApiError ? err.message : 'Could not change that pin.',
                          );
                        }
                      }}
                    >
                      <Star
                        className={`size-4 ${row.favorite ? 'fill-current text-amber-500' : ''}`}
                      />
                    </Button>
                  )}
                  {!weekClosed && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Remove row"
                      onClick={() =>
                        setRows((prev) => {
                          const next = prev.filter((_, j) => j !== i);
                          return next.length > 0 ? next : [blankRow()];
                        })
                      }
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}

            {rows.length === 0 && (
              <tr className="border-t">
                <td colSpan={grid.days.length + 4}>
                  <EmptyState size="sm" art={ClockArt} title="Nothing recorded this week" />
                </td>
              </tr>
            )}
          </tbody>

          <tfoot>
            <tr className="border-t-2 bg-muted/60 font-semibold">
              <td className="px-3 py-2" colSpan={2}>
                Total
              </td>
              {grid.days.map((day) => (
                <td
                  key={day.date}
                  className={`ts-num px-1 py-2 text-center ${divider(day.date)} ${
                    day.isWeekend ? 'text-muted-foreground' : ''
                  }`}
                >
                  {formatDuration(dayTotal(day.date))}
                </td>
              ))}
              <td className="ts-num px-3 py-2 text-right">{formatDuration(weekTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {weekClosed ? (
        <Alert>
          <AlertDescription className="flex items-center gap-2 text-sm">
            {grid.readOnly ? (
              <>
                <Badge variant="outline">read only</Badge>
                {grid.employee.displayName}&rsquo;s week. Corrections are made from their
                own timesheet, or by reopening the week from Approvals.
              </>
            ) : (
              <>
                <Badge variant={grid.periods.every((p) => p.status === 'APPROVED') ? 'default' : 'secondary'}>
                  {grid.periods.every((p) => p.status === 'APPROVED') ? 'approved' : 'waiting for approval'}
                </Badge>
                This week is closed. Ask your supervisor to reopen it if it needs
                correcting.
              </>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {/* The pairings this person actually uses, minus any already on the grid.
              A new week otherwise opens empty and every row means two searches
              through sixty projects - every Monday, for everyone. */}
          {available.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs text-muted-foreground">You often book</span>
              {available.map((s) => (
                <Button hint={"Add this row from your recent work"}
                  key={`${s.projectId}-${s.activityId}`}
                  variant="outline"
                  size="sm"
                  className="h-auto py-1 text-xs font-normal"
                  onClick={() => addSuggested(s)}
                >
                  <Plus className="size-3" />
                  <span className="font-medium">{s.project.name}</span>
                  <span className="text-muted-foreground">· {s.activity.name}</span>
                </Button>
              ))}
            </div>
          )}
        <div className="flex flex-wrap items-center gap-2">
          <Button hint={"Add another project / activity row to this week"} variant="outline" onClick={addRow}>
            <Plus className="size-4" />
            Add a row
          </Button>
          <Button hint={"Save these hours. Submitting the week is a separate step, above"} onClick={() => void submit()} disabled={save.isPending || anyInvalid}>
            <Save className="size-4" />
            {save.isPending ? 'Saving…' : 'Save week'}
          </Button>
          <span className="text-xs text-muted-foreground">
            8:00, 7.5, 1:20 and 90m all work. Empty a cell to remove those hours.
          </span>
        </div>
        {/* Save's outcome is a toast, not a box above the table: Save is at the
            bottom, and a message at the top of a long grid is one nobody sees -
            pressing Save looked like it did nothing. This line stays, because it
            explains a disabled button for as long as it is disabled. */}
        {anyInvalid && (
          <p className="text-sm font-medium text-destructive">
            Save is off until the cells marked in red are fixed: use 8:00, 7.5 or 90m, and no more than a day.
          </p>
        )}
        </div>
      )}
    </div>
  );
}

function weekday(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

/**
 * A cell holds more than a number.
 *
 * The grid draws one duration per cell. An entry may also carry tags, a rate
 * override or its own note, and saving over the cell keeps them - but a field
 * that is stored and never shown implies a guarantee that is absent, which is
 * how the exported flag, isDeduction, and 165 notes and colours were each
 * migrated into a screen that rendered none of them.
 *
 * So the cell says so, and links to the day list, which is the surface those
 * fields are edited on.
 */
function DetailMark({ date }: { date: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={`/timesheet?view=list&week=${date}`}
          aria-label="This day has notes, tags or rates. Open it in the list."
          className="absolute -right-0.5 -top-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground"
        >
          <Info className="size-3" />
        </Link>
      </TooltipTrigger>
      <TooltipContent>
        Also has a note, tag or rate. Open the list to see it.
      </TooltipContent>
    </Tooltip>
  );
}

/** "1 day", "3 days". */
function days(count: number): string {
  return `${count} day${count === 1 ? '' : 's'}`;
}
